import { useId, useMemo } from 'react'

import { cn } from '@/lib/cn'

/**
 * Wybór godziny z listy zamiast pola `input[type=time]`.
 *
 * Natywne pole czasu renderuje się zgodnie z ustawieniami regionalnymi
 * systemu, więc na komputerach z anglojęzycznym Windowsem pokazuje AM/PM.
 * W polskiej szkole „02:00 PM” jest mylące, a samo pole wymaga trafienia
 * kursorem w dwie osobne sekcje. Lista gotowych godzin rozwiązuje jedno
 * i drugie, a przy zajęciach zaczynających się o pełnych kwadransach nie
 * odbiera żadnej swobody.
 */

export const DEFAULT_TIME_STEP = 15
export const DEFAULT_TIME_MIN = '07:00'
export const DEFAULT_TIME_MAX = '22:00'

function toMinutes(value: string): number {
  const [hours, minutes] = value.split(':').map(Number)
  return hours * 60 + minutes
}

function toLabel(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

export interface TimeSelectProps {
  label: string
  value: string
  onChange: (value: string) => void
  /** Krok w minutach. 15 pokrywa wszystkie realne godziny rozpoczęcia zajęć. */
  step?: number
  min?: string
  max?: string
  hint?: string
  error?: string
  disabled?: boolean
  required?: boolean
  /** Ukrywa etykietę wizualnie — używane w gęstych wierszach dostępności. */
  labelHidden?: boolean
  className?: string
}

export function TimeSelect({
  label,
  value,
  onChange,
  step = DEFAULT_TIME_STEP,
  min = DEFAULT_TIME_MIN,
  max = DEFAULT_TIME_MAX,
  hint,
  error,
  disabled,
  required,
  labelHidden = false,
  className,
}: TimeSelectProps) {
  const id = useId()

  const options = useMemo(() => {
    const from = toMinutes(min)
    const to = toMinutes(max)
    const list: string[] = []
    for (let minutes = from; minutes <= to; minutes += step) {
      list.push(toLabel(minutes))
    }
    // Wartość spoza siatki (np. z importu albo starszych danych) musi zostać
    // widoczna, inaczej formularz po cichu podmieniłby godzinę zajęć.
    if (value && !list.includes(value)) {
      list.push(value)
      list.sort()
    }
    return list
  }, [min, max, step, value])

  return (
    <div className={cn('space-y-1.5', className)}>
      <label
        htmlFor={id}
        className={cn('block text-sm font-semibold text-on-surface', labelHidden && 'sr-only')}
      >
        {label}
        {required && !labelHidden && (
          <span className="ml-1 text-primary" aria-hidden="true">
            *
          </span>
        )}
      </label>

      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        aria-invalid={Boolean(error)}
        aria-required={required}
        className={cn(
          'w-full cursor-pointer rounded-lg bg-surface-container-low px-4 py-2.5 text-sm tabular-nums text-on-surface',
          'border-2 border-transparent transition-colors',
          'focus:border-primary focus:bg-surface focus:outline-none',
          'disabled:cursor-not-allowed disabled:opacity-60',
          error && 'border-error',
        )}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>

      {error ? (
        <p className="text-xs font-semibold text-error" role="alert">
          {error}
        </p>
      ) : (
        hint && <p className="text-xs text-on-surface-variant">{hint}</p>
      )}
    </div>
  )
}
